import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Grid,
  Skeleton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { initiatePayment, getPaymentProcessorInfo, formatPrice } from '../services/bookingService';
import { parseISO, format } from 'date-fns';
import EwayPaymentForm from './EwayPaymentForm';
import EwaySecureFieldsPaymentForm from './EwaySecureFieldsPaymentForm';
import EwayPayNowForm from './EwayPayNowForm';
import RawSquarePaymentForm from './RawSquarePaymentForm';
import PortalSquarePaymentForm from './PortalSquarePaymentForm';

const PaymentForm = ({
  clinicId,
  serviceId,
  practitionerId,
  amount,
  patientEmail,
  patientName,
  appointmentDetails,
  onSuccess,
  onCancel,
  returnUrl,
  loading: initialLoading = false
}) => {
  const [loading, setLoading] = useState(initialLoading);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [error, setError] = useState(null);
  const [paymentProcessor, setPaymentProcessor] = useState(null);
  const [processorInfo, setProcessorInfo] = useState(null);
  
  // Check if we should redirect the user to a payment page
  const initiatePaymentProcess = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Extract the clinic stub from the URL
      const pathParts = window.location.pathname.split('/');
      const clinicStub = pathParts[1]; // The first segment after the domain
      
      const paymentData = {
        clinic_id: clinicId,
        clinic_stub: clinicStub, // Include clinic stub for redirect
        appointment_type_id: serviceId,
        practitioner_id: practitionerId,
        amount: amount,
        patient_email: patientEmail,
        patient_name: patientName,
        returnUrl: returnUrl,
        // Include appointment details for auto-creation
        ...appointmentDetails
      };
      
      console.log('Initiating payment with data:', paymentData);
      
      const response = await initiatePayment(paymentData);
      
      setPaymentProcessor(response.processor || 'stripe');
      setPaymentUrl(response.checkout_url);
      
      // Get information about the payment processor
      const info = getPaymentProcessorInfo(response.processor || 'stripe');
      
      // For Square, we need to add the configuration from the response
      if (response.processor === 'square' && response.square_config) {
        info.config = response.square_config;
      }
      
      // For eWay Secure Fields, we need to add the configuration from the response
      if (response.processor === 'eway_secure_fields' && response.eway_config) {
        info.eway_config = response.eway_config;
      }
      
      // For eWay Pay Now button, we need to add the configuration from the response
      if (response.processor === 'eway_paynow' && response.eway_config) {
        info.eway_config = response.eway_config;
      }
      
      setProcessorInfo(info);
      
      console.log('Payment initiated:', response);
      
      // For eWay, eWay Secure Fields, eWay Pay Now, and Square, set loading to false so their forms can render
      // For other processors that require redirect, keep loading true
      if (response.processor === 'eway' || response.processor === 'eway_secure_fields' || response.processor === 'eway_paynow' || response.processor === 'square') {
        setLoading(false);
      }
      // If the payment requires redirect, we don't set loading to false
      // because we want to show waiting state until the user is redirected
    } catch (err) {
      console.error('Error initiating payment:', err);
      setError(err.message || 'Failed to initiate payment process');
      setLoading(false);
    }
  };
  
  // Check for eWay payment processor
  const isEwayPayment = () => {
    // This information would typically come from the Clinic object
    // Here we extract it from the processorInfo if available
    return processorInfo?.name?.toLowerCase() === 'eway';
  };

  // Check for eWay Secure Fields payment processor
  const isEwaySecureFieldsPayment = () => {
    return paymentProcessor === 'eway_secure_fields';
  };

  // Check for eWay Pay Now button payment processor
  const isEwayPayNowPayment = () => {
    return paymentProcessor === 'eway_paynow';
  };

  // Check for Square payment processor
  const isSquarePayment = () => {
    const isSquare = processorInfo?.name?.toLowerCase() === 'square';
    console.log('Checking if Square payment:', {
      processorInfo,
      processorName: processorInfo?.name,
      isSquare
    });
    return isSquare;
  };

  // Automatically initiate payment when component mounts
  useEffect(() => {
    // Comprehensive check for free appointment
    // 1. First convert any string to a number if possible
    let numericAmount = amount;
    if (typeof amount === 'string') {
      numericAmount = parseFloat(amount);
      // If parsing failed (NaN), keep the original value
      if (isNaN(numericAmount)) {
        numericAmount = amount;
      }
    }
    
    // 2. Now do the actual comparison with the numeric value if possible
    const isFreeAppointment = 
      amount === null || 
      amount === 0 || 
      amount === '0' || 
      numericAmount === 0 ||
      numericAmount === 0.0;
    
    console.log('%c PAYMENT FORM AMOUNT ANALYSIS', 'background: #ff8c00; color: black; font-size: 16px; padding: 5px;');
    console.log('- Original amount value:', amount);
    console.log('- Original amount type:', typeof amount);
    console.log('- Numeric amount after conversion:', numericAmount);
    console.log('- Is null check:', amount === null);
    console.log('- Is 0 check:', amount === 0);
    console.log('- Is "0" string check:', amount === '0');
    console.log('- Is numeric 0 check:', numericAmount === 0);
    console.log('- Final free appointment status:', isFreeAppointment);
    
    if (isFreeAppointment) {
      console.log('%c FREE APPOINTMENT DETECTED IN PAYMENT FORM - SKIPPING PAYMENT PROCESS', 'background: #00ff00; color: black; font-size: 16px; padding: 5px;');
      handlePaymentSuccess('free-appointment', null);
      return;
    }
    
    // Always initiate payment to determine processor type first
    if (!paymentUrl && !processorInfo) {
      // Save clinic ID and path info to localStorage for use after redirect
      try {
        // Extract the clinic stub from the URL
        const pathParts = window.location.pathname.split('/');
        const clinicStub = pathParts[1]; // The first segment after the domain
        
        // Save minimal booking info to localStorage
        localStorage.setItem('currentBooking', JSON.stringify({
          clinicId: clinicId,
          clinicStub: clinicStub,
          serviceId: serviceId,
          practitionerId: practitionerId
        }));
        console.log('Saved current booking info to localStorage with clinicStub:', clinicStub);
        
        // Get the full booking data from the parent component
        const bookingSessionData = window.bookingData || {};
        if (Object.keys(bookingSessionData).length === 0) {
          // Try to extract booking data from appointmentDetails
          const bookingData = {
            service: { id: serviceId },
            practitioner: { id: practitionerId },
            appointmentTime: appointmentDetails?.starts_at ? {
              iso: appointmentDetails.starts_at
            } : null,
            patient: {
              first_name: appointmentDetails?.patient_first_name || '',
              last_name: appointmentDetails?.patient_last_name || '',
              email: patientEmail || '',
              phone: appointmentDetails?.patient_phone || ''
            },
            notes: appointmentDetails?.notes || '',
            payment: {
              status: 'pending'
            }
          };
          
          // Save to sessionStorage
          console.log('Saving constructed booking data to sessionStorage');
          sessionStorage.setItem('bookingSession', JSON.stringify(bookingData));
        } else {
          console.log('Saving full booking data to sessionStorage');
          sessionStorage.setItem('bookingSession', JSON.stringify(bookingSessionData));
        }
      } catch (e) {
        console.error('Error saving to storage:', e);
      }
      
      // Initiate payment process to determine processor type
      initiatePaymentProcess();
    }
  }, []);
  
  // Handle redirect to payment processor (but not for eWay, eWay Secure Fields, or Square)
  useEffect(() => {
    if (paymentUrl && !isEwayPayment() && !isEwaySecureFieldsPayment() && !isSquarePayment()) {
      // Short delay before redirecting to ensure UI updates
      const timer = setTimeout(() => {
        window.location.href = paymentUrl;
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [paymentUrl, processorInfo]);
  
  // Handle successful payment
  const handlePaymentSuccess = (transactionId, accessCode) => {
    if (onSuccess) {
      onSuccess({
        status: 'complete',
        transactionId,
        accessCode
      });
    }
  };
  
  // Parse appointment details for display
  const formatAppointmentTime = () => {
    if (!appointmentDetails || !appointmentDetails.starts_at) return 'Unknown time';
    
    const startTime = parseISO(appointmentDetails.starts_at);
    return format(startTime, 'EEEE, MMMM d, yyyy \'at\' h:mm a');
  };
  
  // Render payment summary
  const renderPaymentSummary = () => {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Appointment Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Date & Time
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {formatAppointmentTime()}
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Patient
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {patientName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {patientEmail}
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="h6" color="primary">
                {formatPrice(amount)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };
  
  // For eWay Pay Now button payments, render the dedicated Pay Now form
  if (isEwayPayNowPayment()) {
    console.log('Rendering EwayPayNowForm with config:', paymentProcessor);
    
    const paymentData = {
      clinic_id: clinicId,
      clinic_stub: window.location.pathname.split('/')[1],
      appointment_type_id: serviceId,
      practitioner_id: practitionerId,
      amount: amount,
      patient_email: patientEmail,
      patient_name: patientName,
      returnUrl: returnUrl,
      ...appointmentDetails
    };
    
    return (
      <EwayPayNowForm 
        paymentData={paymentData}
        onPaymentComplete={onSuccess}
        onPaymentError={(error) => setError(error.message || 'Payment failed')}
        ewayConfig={processorInfo?.eway_config || {}}
      />
    );
  }

  // For eWay Secure Fields payments, render the dedicated eWay Secure Fields form
  if (isEwaySecureFieldsPayment()) {
    console.log('Rendering EwaySecureFieldsPaymentForm with config:', paymentProcessor);
    return (
      <EwaySecureFieldsPaymentForm 
        clinicId={clinicId}
        serviceId={serviceId}
        practitionerId={practitionerId}
        amount={amount}
        patientEmail={patientEmail}
        patientName={patientName}
        appointmentDetails={appointmentDetails}
        onSuccess={onSuccess}
        onCancel={onCancel}
        returnUrl={returnUrl}
        ewayConfig={processorInfo?.eway_config || {}}
      />
    );
  }

  // For eWay payments, render the dedicated eWay payment form
  if (isEwayPayment()) {
    return (
      <EwayPaymentForm 
        clinicId={clinicId}
        serviceId={serviceId}
        practitionerId={practitionerId}
        amount={amount}
        patientEmail={patientEmail}
        patientName={patientName}
        appointmentDetails={appointmentDetails}
        onSuccess={onSuccess}
        onCancel={onCancel}
        returnUrl={returnUrl}
        loading={loading}
      />
    );
  }

  // For Square payments, render the portal Square payment form
  if (isSquarePayment()) {
    console.log('Rendering PortalSquarePaymentForm with config:', processorInfo?.config);
    return (
      <PortalSquarePaymentForm 
        clinicId={clinicId}
        serviceId={serviceId}
        practitionerId={practitionerId}
        amount={amount}
        patientEmail={patientEmail}
        patientName={patientName}
        appointmentDetails={appointmentDetails}
        onSuccess={onSuccess}
        onCancel={onCancel}
        returnUrl={returnUrl}
        squareConfig={processorInfo?.config || {}}
      />
    );
  }

  // Main render for non-eWay payment methods
  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            sx={{ mr: 2 }}
            disabled={paymentUrl !== null}
          >
            Back
          </Button>
          <Typography variant="h6">
            Payment
          </Typography>
        </Box>
        
        {renderPaymentSummary()}
        
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          {paymentUrl ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Redirecting to {processorInfo?.name || 'payment page'}...
              </Typography>
              {processorInfo?.logo && (
                <Box sx={{ mt: 2, mb: 3 }}>
                  <img 
                    src={processorInfo.logo} 
                    alt={`${processorInfo.name} logo`}
                    style={{ maxHeight: 40 }} 
                  />
                </Box>
              )}
              <CircularProgress sx={{ mt: 2 }} />
              <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                Please do not close this window
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Preparing Payment...
              </Typography>
              <CircularProgress sx={{ mt: 2 }} />
            </Box>
          )}
        </Paper>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h6">
            Payment
          </Typography>
        </Box>
        
        {renderPaymentSummary()}
        
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            variant="outlined"
            onClick={onCancel}
          >
            Back
          </Button>
          <Button 
            variant="contained"
            onClick={initiatePaymentProcess}
          >
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }
  
  // This should not happen, but just in case
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Payment Processing
      </Typography>
      <CircularProgress />
    </Box>
  );
};

export default PaymentForm;